/**
 * Formats file size in bytes to human readable format
 * @param bytes - Size in bytes
 * @returns Formatted string (e.g., "1.5 MB", "256 KB")
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Gets file extension from filename
 * @param filename - The filename
 * @returns File extension (e.g., "pdf", "jpg")
 */
export const getFileExtension = (filename: string): string => {
    return filename.split('.').pop()?.toLowerCase() || '';
};

/**
 * Checks if file type is an image
 * @param contentType - MIME content type
 * @returns True if image type
 */
export const isImageFile = (contentType: string): boolean => {
    return contentType.startsWith('image/');
};

/**
 * Checks if file type is a PDF
 * @param contentType - MIME content type
 * @returns True if PDF type
 */
export const isPdfFile = (contentType: string): boolean => {
    return contentType === 'application/pdf';
};

/**
 * Checks if file type is a video
 * @param contentType - MIME content type
 * @returns True if video type
 */
export const isVideoFile = (contentType: string): boolean => {
    return contentType.startsWith('video/');
};

/**
 * Checks if a file type can be previewed inline (image, video, or PDF)
 * @param contentType - MIME content type
 * @returns True if the file can be shown in an in-app preview
 */
export const isPreviewableFile = (contentType: string): boolean => {
    return isImageFile(contentType) || isVideoFile(contentType) || isPdfFile(contentType);
};

/**
 * Gets a user-friendly file type name
 * @param contentType - MIME content type
 * @returns User-friendly type name
 */
export const getFileTypeName = (contentType: string): string => {
    if (contentType.includes('pdf')) return 'PDF';
    if (contentType.includes('image')) return 'Image';
    if (contentType.includes('word') || contentType.includes('document')) return 'Word Document';
    if (contentType.includes('sheet') || contentType.includes('excel')) return 'Excel Sheet';
    if (contentType.includes('text')) return 'Text File';
    if (contentType.includes('zip') || contentType.includes('archive')) return 'Archive';

    return contentType.split('/')[1]?.toUpperCase() || 'File';
}; 